import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import TradingChart from './components/TradingChart';
import OrderBook from './components/OrderBook';
import TradingForm from './components/TradingForm';
import { supabase } from '../../lib/supabase';

interface TradingModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'buy' | 'sell';
  pair: string;
  currentPrice: number;
}

const TradingModal = ({ isOpen, onClose, type, pair, currentPrice }: TradingModalProps) => {
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState(currentPrice.toString());
  const [total, setTotal] = useState('');
  const [loading, setLoading] = useState(false);
  const [balances, setBalances] = useState({ usdt: 0, btc: 0, pep: 0 });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (isOpen) {
      fetchBalances();
      setPrice(currentPrice.toString());
    }
  }, [isOpen, currentPrice]);

  useEffect(() => {
    if (orderType === 'market') {
      setPrice(currentPrice.toString());
    }
  }, [orderType, currentPrice]);

  useEffect(() => {
    if (amount && price) {
      const calculatedTotal = (parseFloat(amount) * parseFloat(price)).toFixed(2);
      setTotal(calculatedTotal);
    }
  }, [amount, price]);

  const fetchBalances = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch from all sources
      const [walletsRes, assetsRes, profileRes] = await Promise.all([
        supabase.from('user_wallets').select('*').eq('user_id', user.id).single(),
        supabase.from('user_assets').select('*').eq('user_id', user.id),
        supabase.from('profiles').select('pepcoin_balance').eq('id', user.id).single()
      ]);

      const usdtBalance = walletsRes.data?.usdt_balance || 0;
      const btcBalance = walletsRes.data?.btc_balance || 0;
      
      const pepAsset = assetsRes.data?.find((a: any) => a.symbol === 'PEP');
      const pepFromAssets = pepAsset?.balance || 0;
      const pepFromProfile = profileRes.data?.pepcoin_balance || 0;
      const pepBalance = Math.max(pepFromAssets, pepFromProfile);

      setBalances({ usdt: usdtBalance, btc: btcBalance, pep: pepBalance });
    } catch (error) {
      console.error('Error fetching balances:', error);
    }
  };

  const handleTrade = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid amount' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Please sign in to trade');

      const tradeAmount = parseFloat(amount);
      const tradePrice = parseFloat(price);
      const tradeTotal = parseFloat(total);

      // Check balances
      if (type === 'buy') {
        if (balances.usdt < tradeTotal) {
          setMessage({ type: 'error', text: 'Insufficient USDT balance' });
          setLoading(false);
          return;
        }
      } else {
        const cryptoBalance = pair === 'BTC/USDT' ? balances.btc : balances.pep;
        if (cryptoBalance < tradeAmount) {
          setMessage({ type: 'error', text: `Insufficient ${pair.split('/')[0]} balance` });
          setLoading(false);
          return;
        }
      }

      // Get current wallet
      const { data: wallet } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (!wallet) throw new Error('Wallet not found');

      let newUsdtBalance = wallet.usdt_balance;
      let newBtcBalance = wallet.btc_balance;
      let newPepBalance = balances.pep;

      if (type === 'buy') {
        newUsdtBalance -= tradeTotal;
        if (pair === 'BTC/USDT') {
          newBtcBalance += tradeAmount;
        } else {
          newPepBalance += tradeAmount;
        }
      } else {
        newUsdtBalance += tradeTotal;
        if (pair === 'BTC/USDT') {
          newBtcBalance -= tradeAmount;
        } else {
          newPepBalance -= tradeAmount;
        }
      }

      // Update wallet
      await supabase
        .from('user_wallets')
        .update({
          usdt_balance: newUsdtBalance,
          btc_balance: newBtcBalance,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      // Update PepCoin in assets and profile
      if (pair === 'PEP/USDT') {
        await Promise.all([
          supabase
            .from('user_assets')
            .upsert({
              user_id: user.id,
              symbol: 'PEP',
              name: 'PepCoin',
              balance: newPepBalance,
              updated_at: new Date().toISOString()
            }),
          supabase
            .from('profiles')
            .update({ pepcoin_balance: newPepBalance })
            .eq('id', user.id)
        ]);
      }

      // Record transaction
      await supabase.from('transactions').insert({
        user_id: user.id,
        type: type === 'buy' ? 'deposit' : 'withdrawal',
        amount: tradeAmount,
        currency: pair.split('/')[0],
        status: 'completed',
        description: `${type.toUpperCase()} ${tradeAmount} ${pair.split('/')[0]} at ${tradePrice} USDT`,
        created_at: new Date().toISOString()
      });

      setMessage({ 
        type: 'success', 
        text: `Successfully ${type === 'buy' ? 'bought' : 'sold'} ${tradeAmount} ${pair.split('/')[0]}!` 
      });

      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);

    } catch (error: any) {
      console.error('Trade error:', error);
      setMessage({ type: 'error', text: error.message || 'Trade failed' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white">
            {type === 'buy' ? 'Buy' : 'Sell'} {pair}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Balances */}
        <div className="bg-gray-800 rounded-lg p-4 mb-4">
          <div className="text-sm text-gray-400 mb-2">Available Balance</div>
          <div className="space-y-1">
            <div className="flex justify-between text-white">
              <span>USDT:</span>
              <span className="font-bold">{balances.usdt.toFixed(2)}</span>
            </div>
            {pair === 'BTC/USDT' && (
              <div className="flex justify-between text-white">
                <span>BTC:</span>
                <span className="font-bold">{balances.btc.toFixed(8)}</span>
              </div>
            )}
            {pair === 'PEP/USDT' && (
              <div className="flex justify-between text-white">
                <span>PEP:</span>
                <span className="font-bold">{balances.pep.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Order Type */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setOrderType('market')}
            className={`flex-1 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
              orderType === 'market'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Market
          </button>
          <button
            onClick={() => setOrderType('limit')}
            className={`flex-1 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
              orderType === 'limit'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Limit
          </button>
        </div>

        {/* Price Input */}
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">
            Price (USDT)
          </label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            disabled={orderType === 'market'}
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            placeholder="0.00"
          />
        </div>

        {/* Amount Input */}
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">
            Amount ({pair.split('/')[0]})
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        {/* Total */}
        <div className="mb-6">
          <label className="block text-sm text-gray-400 mb-2">
            Total (USDT)
          </label>
          <input
            type="number"
            value={total}
            onChange={(e) => {
              setTotal(e.target.value);
              if (price && parseFloat(price) > 0) {
                setAmount((parseFloat(e.target.value) / parseFloat(price)).toFixed(8));
              }
            }}
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {message.text}
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleTrade}
          disabled={loading || !amount || !price}
          className={`w-full py-3 rounded-lg font-bold text-white transition-colors cursor-pointer whitespace-nowrap ${
            type === 'buy'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-red-600 hover:bg-red-700'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {loading ? 'Processing...' : `${type === 'buy' ? 'Buy' : 'Sell'} ${pair.split('/')[0]}`}
        </button>
      </div>
    </div>
  );
};

export default function TradingPage() {
  const [selectedPair, setSelectedPair] = useState('BTC/USDT');
  const [currentPrice, setCurrentPrice] = useState(43250.50);
  const [priceChange, setPriceChange] = useState(2.34);
  const [modalOpen, setModalOpen] = useState(false);
  const [tradeType, setTradeType] = useState<'buy' | 'sell'>('buy');

  const pairs = [
    { symbol: 'BTC/USDT', price: 43250.50, change: 2.34 },
    { symbol: 'ETH/USDT', price: 2280.75, change: -1.23 },
    { symbol: 'PEP/USDT', price: 0.0125, change: 5.67 }
  ];

  useEffect(() => {
    const pair = pairs.find(p => p.symbol === selectedPair);
    if (pair) {
      setCurrentPrice(pair.price);
      setPriceChange(pair.change);
    }
  }, [selectedPair]);

  const openTradeModal = (type: 'buy' | 'sell') => {
    setTradeType(type);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Trading</h1>
          <p className="text-gray-400">Trade cryptocurrencies with real-time market data</p>
        </div>

        {/* Trading Pairs */}
        <div className="max-w-7xl mx-auto mb-6">
          <div className="bg-gray-900 rounded-xl p-4">
            <div className="flex gap-4 overflow-x-auto">
              {pairs.map((pair) => (
                <button
                  key={pair.symbol}
                  onClick={() => setSelectedPair(pair.symbol)}
                  className={`flex-shrink-0 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                    selectedPair === pair.symbol
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <div className="font-bold">{pair.symbol}</div>
                  <div className="text-sm">${pair.price.toLocaleString()}</div>
                  <div className={`text-xs ${pair.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {pair.change >= 0 ? '+' : ''}{pair.change}%
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Trading Interface */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart & Order Book */}
          <div className="lg:col-span-2 space-y-6">
            <TradingChart pair={selectedPair} />
            <OrderBook currentPrice={currentPrice} priceChange={priceChange} />
          </div>

          {/* Trading Form */}
          <div className="space-y-6">
            <div className="bg-gray-900 rounded-xl p-6">
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => openTradeModal('buy')}
                  className="flex-1 py-2 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap bg-green-600 text-white hover:bg-green-700"
                >
                  Buy
                </button>
                <button
                  onClick={() => openTradeModal('sell')}
                  className="flex-1 py-2 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap bg-red-600 text-white hover:bg-red-700"
                >
                  Sell
                </button>
              </div>

              <TradingForm pair={selectedPair} onTrade={openTradeModal} />
            </div>
          </div>
        </div>
      </div>

      <TradingModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        type={tradeType}
        pair={selectedPair}
        currentPrice={currentPrice}
      />
    </div>
  );
}
